/**
 * Cliente Storefront (navegador) para el checkout client-side.
 *
 * Cuando `runtime_controls.checkout_client_side_enabled` está ON, el navegador
 * resuelve el selling plan (query `sellingPlanAllocations`) y crea el carrito
 * Shopify (mutación `cartCreate`) DIRECTAMENTE contra el Storefront API, para
 * que cada checkout salga de la IP del comprador y no de la única IP del
 * servidor (evita el bloqueo 430 de Shopify).
 *
 * Usa `fetch` (no axios) porque es una llamada cross-origin directa a Shopify
 * con el token público de Storefront en la cabecera.
 *
 * La heurística de selección de plan (`resolveSellingPlan`) y el encoder
 * (`encodeMonthlySellingPlans`) son PORTES byte-a-byte del backend Python
 * (`shopify_storefront_client.get_best_selling_plan_id_for_variant` y
 * `helpers._encode_selling_plan_map`) para que el carrito creado client-side
 * sea idéntico al que crearía el servidor.
 */

import { OutOfStockError } from './client';

// ─── Tipos ───────────────────────────────────────────────────────────────────

/** Nodo de `sellingPlanAllocations.edges[].node` (query literal más abajo). */
export interface SellingPlanAllocation {
  checkoutChargeAmount: { amount: string | null } | null;
  sellingPlan: { id: string; name: string } | null;
}

export interface ResolveSellingPlanOptions {
  /** Descuento objetivo (0..1). Junto a basePrice activa el PATH 0. */
  targetDiscount?: number;
  /** Precio unitario base (sin descuento). */
  basePrice?: number;
  preferTrial?: boolean;
  requireTrial?: boolean;
  /**
   * Se invoca cuando el mejor candidato del PATH 0 se aleja de lo esperado más
   * de la tolerancia. NO aborta: `resolveSellingPlan` devuelve el best igual
   * (paridad estricta con Python). Se usa para reportar
   * 'selling_plan_discount_mismatch' vía checkout-callback.
   */
  onMismatch?: () => void;
}

export interface CartLineInput {
  merchandiseId: string;
  quantity: number;
  sellingPlanId?: string;
  attributes?: Array<{ key: string; value: string }>;
}

export interface CartAttributeInput {
  key: string;
  value: string | null | undefined;
}

export interface CreateCartArgs {
  graphqlUrl: string;
  token: string;
  buyerEmail: string;
  lines: CartLineInput[];
  attributes?: CartAttributeInput[];
}

export interface CreatedCart {
  id: string;
  checkoutUrl: string;
}

export interface StorefrontRetryConfig {
  maxAttempts?: number;
  baseDelayMs?: number;
  /** Inyectable en tests para saltar los delays reales. */
  sleep?: (ms: number) => Promise<void>;
}

export type StorefrontErrorCode =
  | 'network'
  | 'ip_blocked'
  | 'auth'
  | 'throttled'
  | 'graphql'
  | 'cart_create_user_error';

/**
 * Error tipado del Storefront API. NO se debe reintentar como error de negocio
 * (los throttles ya se reintentan internamente antes de lanzarse). Step13 lo
 * muestra al usuario vía su `catch` común.
 */
export class StorefrontError extends Error {
  readonly code: StorefrontErrorCode;
  readonly status?: number;

  constructor(code: StorefrontErrorCode, message: string, status?: number) {
    super(message);
    this.name = 'StorefrontError';
    this.code = code;
    this.status = status;
  }
}

// ─── GraphQL literals (espejo del backend) ───────────────────────────────────

const SELLING_PLAN_ALLOCATIONS_QUERY = `
query VariantSellingPlans($id: ID!) {
  node(id: $id) {
    ... on ProductVariant {
      id
      sellingPlanAllocations(first: 10) {
        edges {
          node {
            checkoutChargeAmount { amount }
            sellingPlan { id name }
          }
        }
      }
    }
  }
}`;

const CART_CREATE_MUTATION = `
mutation CartCreate($input: CartInput!) {
  cartCreate(input: $input) {
    cart { id checkoutUrl }
    userErrors { field message }
  }
}`;

// ─── Transporte GraphQL (fetch + reintento por throttle) ─────────────────────

interface GraphqlResponse<T> {
  data?: T;
  errors?: Array<{ message?: string; extensions?: { code?: string } }>;
}

const defaultSleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/** Backoff exponencial con jitter: ~1s, 2s, 4s (base 1000). */
function backoffDelay(baseDelayMs: number, attempt: number): number {
  const delay = baseDelayMs * Math.pow(2, attempt - 1);
  const jitter = Math.random() * baseDelayMs;
  return delay + jitter;
}

async function storefrontGraphql<T>(
  graphqlUrl: string,
  token: string,
  body: { query: string; variables?: Record<string, unknown> },
  retry?: StorefrontRetryConfig,
): Promise<T> {
  const maxAttempts = retry?.maxAttempts ?? 3;
  const baseDelayMs = retry?.baseDelayMs ?? 1000;
  const sleep = retry?.sleep ?? defaultSleep;

  let attempt = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    attempt += 1;

    let resp: Response;
    try {
      resp = await fetch(graphqlUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify(body),
      });
    } catch {
      throw new StorefrontError('network', 'No se pudo conectar con Shopify para crear el checkout.');
    }

    // 430: Shopify Security Rejection (la IP está bloqueada). Es exactamente el
    // fallo que este flujo client-side pretende evitar → mensaje claro.
    if (resp.status === 430) {
      throw new StorefrontError(
        'ip_blocked',
        'Shopify ha bloqueado temporalmente la conexión (430). Inténtalo de nuevo en unos minutos.',
        430,
      );
    }

    if (resp.status === 401 || resp.status === 403) {
      throw new StorefrontError('auth', `Autenticación con Shopify fallida (HTTP ${resp.status}).`, resp.status);
    }

    // 429: throttle explícito por HTTP → reintento con backoff (igual que THROTTLED).
    if (resp.status === 429) {
      if (attempt < maxAttempts) {
        await sleep(backoffDelay(baseDelayMs, attempt));
        continue;
      }
      throw new StorefrontError('throttled', 'Shopify está limitando las peticiones (429). Inténtalo de nuevo.', 429);
    }

    let json: GraphqlResponse<T> | null = null;
    try {
      json = (await resp.json()) as GraphqlResponse<T>;
    } catch {
      json = null;
    }

    if (!resp.ok) {
      throw new StorefrontError('graphql', `Shopify respondió HTTP ${resp.status}.`, resp.status);
    }

    // HTTP 200 con errores GraphQL: THROTTLED → reintento; cualquier otro → error.
    const errors = json?.errors;
    if (Array.isArray(errors) && errors.length > 0) {
      const throttled = errors.some((e) => e?.extensions?.code === 'THROTTLED');
      if (throttled) {
        if (attempt < maxAttempts) {
          await sleep(backoffDelay(baseDelayMs, attempt));
          continue;
        }
        throw new StorefrontError(
          'throttled',
          'Shopify está limitando las peticiones (THROTTLED). Inténtalo de nuevo.',
        );
      }
      throw new StorefrontError('graphql', String(errors[0]?.message || 'Error de Shopify Storefront.'));
    }

    return (json?.data ?? ({} as T)) as T;
  }
}

// ─── API pública ─────────────────────────────────────────────────────────────

/** Consulta los sellingPlanAllocations (first: 10) de una variante. */
export async function fetchSellingPlanAllocations(
  graphqlUrl: string,
  token: string,
  variantGid: string,
  retry?: StorefrontRetryConfig,
): Promise<SellingPlanAllocation[]> {
  type AllocData = {
    node?: { sellingPlanAllocations?: { edges?: Array<{ node?: SellingPlanAllocation | null } | null> } | null } | null;
  };
  const data = await storefrontGraphql<AllocData>(
    graphqlUrl,
    token,
    { query: SELLING_PLAN_ALLOCATIONS_QUERY, variables: { id: variantGid } },
    retry,
  );
  const edges = data?.node?.sellingPlanAllocations?.edges ?? [];
  return edges.map((edge) => edge?.node ?? null).filter((node): node is SellingPlanAllocation => Boolean(node));
}

/**
 * Selecciona el mejor sellingPlanId. PORTE byte-a-byte de Python
 * `get_best_selling_plan_id_for_variant`:
 *   PATH 0 (domina en checkout): con target_discount+base_price, entre candidatos
 *           con checkoutChargeAmount > 1e-9 (excluye trials), argmin de
 *           |amount − base·(1−target)|. Si supera tolerancia → onMismatch (pero
 *           devuelve best igualmente). tolerancia = max(0.01, base·0.01).
 *   PATH 1 (solo preferTrial): primer candidato con |amount| < 1e-9.
 *   requireTrial y sin trial → null.
 *   PATH 2: primer candidato cuyo nombre (upper) contenga "MES" o "MONTH".
 *   PATH 3: primer candidato.
 *   sin candidatos → null.
 */
export function resolveSellingPlan(
  allocations: SellingPlanAllocation[],
  options: ResolveSellingPlanOptions = {},
): string | null {
  const { targetDiscount, basePrice, preferTrial = false, requireTrial = false, onMismatch } = options;

  const candidates: Array<{ id: string; name: string; amount: number | null }> = [];
  for (const alloc of allocations ?? []) {
    const sp = alloc?.sellingPlan;
    const id = sp?.id ? String(sp.id) : '';
    if (!id) continue;
    const name = String(sp?.name ?? '');
    const rawAmt = alloc?.checkoutChargeAmount?.amount;
    let amount: number | null;
    if (rawAmt == null || String(rawAmt).trim() === '') {
      amount = null;
    } else {
      const parsed = Number(rawAmt);
      amount = Number.isFinite(parsed) ? parsed : null;
    }
    candidates.push({ id, name, amount });
  }

  if (candidates.length === 0) return null;

  // PATH 0: selección por % de descuento (modelo B).
  if (targetDiscount != null && typeof basePrice === 'number' && basePrice > 0) {
    const expected = basePrice * (1 - targetDiscount);
    const priced = candidates.filter(
      (c): c is { id: string; name: string; amount: number } => c.amount != null && c.amount > 1e-9,
    );
    if (priced.length > 0) {
      let best = priced[0];
      let bestDist = Math.abs(best.amount - expected);
      for (let i = 1; i < priced.length; i += 1) {
        const dist = Math.abs(priced[i].amount - expected);
        if (dist < bestDist) {
          best = priced[i];
          bestDist = dist;
        }
      }
      const tolerance = Math.max(0.01, basePrice * 0.01);
      if (Math.abs(best.amount - expected) > tolerance && onMismatch) {
        try {
          onMismatch();
        } catch {
          /* best-effort: la telemetría nunca rompe la selección */
        }
      }
      return best.id;
    }
  }

  // PATH 1: trial / primer cobro 0.
  if (preferTrial) {
    for (const c of candidates) {
      if (c.amount != null && Math.abs(c.amount) < 1e-9) return c.id;
    }
  }
  if (requireTrial) return null;

  // PATH 2: heurística por nombre.
  for (const c of candidates) {
    const upper = c.name.toUpperCase();
    if (upper.includes('MES') || upper.includes('MONTH')) return c.id;
  }

  // PATH 3: fallback.
  return candidates[0].id;
}

const STOCK_ERROR_PATTERN = /stock|agotad|inventor|out of stock|sold out|no disponible|not available|unavailable/i;

function isStockUserError(message: string): boolean {
  return STOCK_ERROR_PATTERN.test(String(message || ''));
}

/**
 * Crea el carrito (mutación cartCreate). PORTE literal de Python `create_cart`:
 * input = { lines, attributes:[{key,value}] (omite value null), buyerIdentity:{email} }.
 * userErrors no vacío → error (los de stock/availability se mapean a
 * OutOfStockError para que Step13 rebote a selección de recetas).
 */
export async function createCart(args: CreateCartArgs, retry?: StorefrontRetryConfig): Promise<CreatedCart> {
  const { graphqlUrl, token, buyerEmail, lines, attributes } = args;

  const attrList = (attributes ?? [])
    .filter((a) => a && a.key && a.value != null)
    .map((a) => ({ key: String(a.key), value: String(a.value) }));

  type CartCreateData = {
    cartCreate?: {
      cart?: CreatedCart | null;
      userErrors?: Array<{ field?: string[] | null; message?: string }>;
    } | null;
  };

  const data = await storefrontGraphql<CartCreateData>(
    graphqlUrl,
    token,
    {
      query: CART_CREATE_MUTATION,
      variables: { input: { lines, attributes: attrList, buyerIdentity: { email: buyerEmail } } },
    },
    retry,
  );

  const cartCreate = data?.cartCreate ?? {};
  const userErrors = cartCreate.userErrors ?? [];
  if (userErrors.length > 0) {
    const message = userErrors[0]?.message || 'No se pudo crear el carrito en Shopify.';
    if (isStockUserError(message)) {
      throw new OutOfStockError(message, []);
    }
    throw new StorefrontError('cart_create_user_error', String(message));
  }

  const cart = cartCreate.cart ?? null;
  return { id: cart?.id ?? '', checkoutUrl: cart?.checkoutUrl ?? '' };
}

/**
 * Codifica {variantNumeric: sellingPlanGid} para el atributo de carrito
 * `monthly_selling_plans`. PORTE IDÉNTICO de Python `_encode_selling_plan_map`:
 * pares `variantNumeric=sellingPlanGid` unidos por `|`, omitiendo entradas con
 * variante o plan vacíos. La clave ya es variant_numeric.
 * Ej: {"123":"gid://…/30","456":"gid://…/31"} → "123=gid://…/30|456=gid://…/31".
 */
export function encodeMonthlySellingPlans(map: Record<string, string>): string {
  const parts: string[] = [];
  for (const [variant, plan] of Object.entries(map || {})) {
    const v = String(variant ?? '').trim();
    const p = String(plan ?? '').trim();
    if (v && p) parts.push(`${v}=${p}`);
  }
  return parts.join('|');
}
